import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { AccountService } from './services/account.service';
import { StorageManagerService } from './services/storage-manager.service';
import { fromEvent } from 'rxjs';
import { ChannelsService } from './services/channels.service';
import { UiService } from './services/ui.service';
import { GeoblockerService } from './services/geoblocker.service';
import { GenericModalSettings } from './models/generic-modal-settings';
import { throttleTime } from 'rxjs/operators';
import { UserInfo } from './models';
import { LocalisationService } from './services/localisation.service';
import { RemoteConfigService } from './services/remote-config.service';
import { DeviceManagementService } from './services/device-management.service';

declare let tizen: any;
declare var webapis: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {

    version = "190c14b7"; // automated version number

    avatarUrl: string;
    loading = false;
    patreon = false;
    showMenu = false;
    playstation = false;
    simpleAlert: any;
    userInfo: any;
    userHold = false;
    blockAccess: boolean;
    showGenericModal = false;

    public isPlayerPage = false;
    public previousUrl = '';
    public currentUrl = '';

    private seasonSource = '/home';

    @ViewChild('dpadContainer') dpadContainer: { nativeElement: { dispatchEvent: (arg0: KeyboardEvent) => void; }; };


    constructor(
        private router: Router,
        private channelService: ChannelsService,
        private storageManager: StorageManagerService,
        private accountService: AccountService,
        private changeDetector: ChangeDetectorRef,
        private geoblockerService: GeoblockerService,
        private ui: UiService,
        private localisationService: LocalisationService,
        private config: RemoteConfigService,
        private device: DeviceManagementService) {

        fromEvent(window, 'popstate')
            .pipe(throttleTime(500))
            .subscribe((x) => {
                let doClose = this.router.url.includes('home') && location.href.includes('home');
                doClose = doClose || this.router.url === '/';
                this.handleBackKey(doClose, true);
            });

        this.tizenNetworking();
        this.tizenRemote();

        this.geoblockerService.isBlocked().then(x => {
            this.blockAccess = x;
        });

        if (this.isAuthenticated()) {
            setTimeout(() => {
                this.getUserInfo();
                this.channelService.refreshUserChannels();
                this.device.addDevice().subscribe();
            }, 150);
        }

        router.events.subscribe((routerEvent: RouterEvent) => {
            this.checkRouterEvent(routerEvent);
        });

        fromEvent(document, 'tizenhwkey').subscribe((e: any) => {
            if (e.keyName === 'back') {
                this.handleBackKey();
            }
        });

        fromEvent(document, 'keyup').subscribe((e: KeyboardEvent) => {
            if (e.key === 'Backspace') {
                this.handleBackKey();
            }
        });

        if (this.playstation) {
            console.log = (message) => {
                const log = document.getElementById('logger');
                const msg = document.createElement('div');
                msg.textContent = message;
                log.appendChild(msg);
            };

            console.error = (message) => {
                const log = document.getElementById('logger');
                const msg = document.createElement('div');
                msg.textContent = message;
                log.appendChild(msg);
            };
        }
    }

    async ngOnInit(): Promise<void> {
        this.addFavicon();
        this.ui.loading.subscribe(x => x ? this.startLoading() : this.stopLoading());
        this.ui.genericModal.subscribe(isActive => {
            this.showGenericModal = isActive;
        });
        localStorage.removeItem('upnext-recommendation-id');
        this.localisationService.refreshTranslations();
    }

    addFavicon(): void {
        const iconPath = this.config.remoteConfig.stv_favicon;
        const link: HTMLLinkElement = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/x-icon';
        link.href = iconPath;
        document.head.appendChild(link);
    }

    tizenNetworking(): void {
        try {
            if (webapis !== undefined && webapis !== null) {
                try {
                    webapis.network.addNetworkStateChangeListener((value) => {
                        if (!this.router.url.includes('video')) {
                            if (value === webapis.network.NetworkState.GATEWAY_DISCONNECTED) {
                                this.networkingLost();
                            } else if (value === webapis.network.NetworkState.GATEWAY_CONNECTED) {
                                this.networkingReload();
                            }
                        }
                    });

                } catch { }
                return;
            }
        } catch { }
    }

    tizenRemote(): void {
        try {
            if (webapis !== undefined && webapis !== null) {
                tizen.tvinputdevice.registerKeyBatch(
                    [
                        'MediaStop',
                        'MediaPlayPause',
                        'MediaPlay',
                        'MediaPause',
                        'MediaFastForward',
                        'MediaRewind',
                        'MediaTrackPrevious',
                        'MediaTrackNext'
                    ]);
            }
        } catch { }
    }

    handleBackKey(doClose = true, isPopState = false): void {

        if (this.router.url.includes('video') || (this.router.url.includes('settings') && isPopState)) {
            return;
        }

        if (this.ui.isModalOpen) {
            this.ui.stopShowingGenericModal();
            return;
        }

        if (this.router.url.includes('signup') || this.router.url.includes('login')) {
            this.accountService.goHome = true;
            this.router.navigate(['']);
            return;
        }

        if (this.router.url.includes('season')) {
            this.router.navigate([this.seasonSource]);
            return;
        }

        if ((this.router.url === '/' || this.router.url.includes('home')) && doClose) {
            this.showCloseAppModal();
            return;
        }

        if (!isPopState) {
            history.back();
        }
    }

    showCloseAppModal(): void {
        const lastFocus = document.activeElement;
        let isTizen = false;
        try {
            isTizen = tizen !== undefined && tizen !== null;
        } catch { }

        const cb = () => (lastFocus as any).focus();
        const closeAppCb = () => {
            try {
                if (isTizen) {
                    tizen.application.getCurrentApplication().exit();
                    return;
                }
            } catch {
            }

            if ((window as any).chrome && (window as any).chrome.webview) {
                (window as any).chrome.webview.postMessage("closeApp");
                return;
            }

            window.close();
        };

        const options: GenericModalSettings = {
            title: 'Closing App',
            body: 'Would you like to exit the App?',
            close: true,
            closeLabel: 'CANCEL',
            elementFocusCallback: cb,
            okCallback: closeAppCb,
            okLabel: 'YES'
        };
        this.ui.setGenericModalSettings(options);
        this.ui.showGenericModal();
    }

    networkingLost(): void {
        const lastFocus = document.activeElement;
        let isTizen = false;
        try {
            isTizen = tizen !== undefined && tizen !== null;
        } catch { }

        const lastFocusCallback = () => (lastFocus as any).focus();

        const options: GenericModalSettings = {
            title: 'Network Error',
            body: 'Failed to connect. Please check your device network connection and try again.',
            close: true,
            closeLabel: 'OK',
            elementFocusCallback: lastFocusCallback,
        };
        this.ui.setGenericModalSettings(options);
        this.ui.showGenericModal();
    }

    networkingReload(): void {
        const lastFocus = document.activeElement;
        let isTizen = false;
        try {
            isTizen = tizen !== undefined && tizen !== null;
        } catch { }

        const lastFocusCallback = () => (lastFocus as any).focus();
        const reloadPageCallback = () => {
            if (isTizen) {
                this.reloadCurrentRoute();
                return;
            }
        };

        const options: GenericModalSettings = {
            title: 'Network Restored',
            body: 'Network Connection renabled, would you like to reload the app?',
            close: true,
            closeLabel: 'CANCEL',
            elementFocusCallback: lastFocusCallback,
            okCallback: reloadPageCallback,
            okLabel: 'RELOAD'
        };
        this.ui.setGenericModalSettings(options);
        this.ui.showGenericModal();
    }

    reloadCurrentRoute(): void {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([currentUrl]);
        });
    }

    // closeApp() {
    //     try {
    //         if (this.isTizen) {
    //             tizen.application.getCurrentApplication().exit();
    //             return;
    //         }
    //     } catch {
    //     }
    //
    //     window.close();
    // }

    checkRouterEvent(routerEvent: RouterEvent): void {
        if (routerEvent instanceof NavigationStart) {
            this.startLoading();
        }

        if (routerEvent instanceof NavigationEnd) {

            if (routerEvent.url.includes('/season/') && (this.previousUrl.includes('home') || this.previousUrl.includes('shows'))) {
                this.seasonSource = this.previousUrl;
            }

            this.isPlayerPage = this.isVideoPage(routerEvent);
            if (!this.isPlayerPage) {
                this.previousUrl = routerEvent.url;
            }
            this.currentUrl = routerEvent.url;
            this.stopLoading();
        }
    }

    private isVideoPage(routerEvent: RouterEvent): boolean {
        return routerEvent.url.includes('/video') || routerEvent.url.includes('live') || routerEvent.url.includes('blank');
    }

    public startLoading(): void {
        this.loading = true;
        this.changeDetector.detectChanges();
    }

    public stopLoading(): void {
        this.loading = false;
        this.changeDetector.detectChanges();
    }

    public hasSubscription(): boolean {
        const sub = this.storageManager.getSubscription();
        if ((sub + '') !== 'null') {
            return !!sub;
        }
        return false;
    }

    public getAvatarUrl(): string {
        return this.avatarUrl;
    }

    closeMenu(): void {
        this.showMenu = false;
    }

    signout(): void {
        this.closeMenu();
        this.patreon = false;
        this.userInfo = null;
        this.avatarUrl = null;
        this.accountService.logoutUser();
    }

    public getUserInfo(): Promise<string> {
        return new Promise((resolve, reject) => {
            this.accountService.updateToken().then(
                () => {
                    if (this.userHold === false) {
                        this.accountService.getUserInfoQuery('avatar,hash,patreon,preferences,points,subscription').subscribe({
                            next: (res: UserInfo) => {
                                this.userInfo = res;
                                this.storageManager.setSubscription(res.hasSubscription);
                                this.patreon = res.hasPatreon;
                                this.avatarUrl = res.avatarUrl;
                                this.storageManager.setAvatarUrl(res.avatarUrl);
                                resolve('Complete');
                            },
                            error: error => {
                                console.log('getUserInfo er2');
                                this.accountService.checkUnauthorized(error);
                                resolve('Complete');
                            }
                        });
                    }
                }, err => {
                    console.log('getUserInfo er1');
                    console.log(err);
                });
        });
    }

    public isAuthenticated(): boolean {
        return this.accountService.isAuthenticated();
    }

    showSimpleAlert(alert: any): void {
        // console.log("EXISTING")
        // console.log(this.simpleAlert)
        this.simpleAlert = alert;
        // console.log("NEW")
        // console.log(this.simpleAlert)
    }

    @HostListener('wheel', ['$event'])
    onScrollWheel(event: any): void {
        event.preventDefault();
        event.stopPropagation();
        if (event.deltaY > 0) {
            this.dpadContainer.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown' }));
            return;
        }
        if (event.deltaY < 0) {
            this.dpadContainer.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp' }));
            return;
        }
        if (event.deltaX > 0) {
            this.dpadContainer.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight' }));
            return;
        }
        if (event.deltaX < 0) {
            this.dpadContainer.nativeElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft' }));
            return;
        }
    }
}
